import { CollapseAllVisibility, IColumn, IDetailsGroupDividerProps, IDetailsRowProps, IGroup } from '@fluentui/react';
import {
  addGroupItems,
  adjustColumnWidth,
  Checkbox,
  checkboxStylesCircle,
  CommonStyles,
  debounce,
  defaultSemanticColors,
  detailsHeaderStylesGroup,
  DetailsList,
  detailsListStylesGroup,
  detailsListStylesGroupCard,
  EmptyMessage,
  FontWeights,
  getGroupHeight,
  getGroupsWithEmptyGroupsOfNextPage,
  getRowWidthByColumns,
  GroupsAndItems,
  IColumnWithDataType,
  IDetailsListProps,
  IGroupWithDataType,
  Link,
  linkStylesGhost,
  ListRow,
  listRowStylesThin,
  ListRowType,
  Loader,
  loaderStylesSpinnerXLarge,
  RouterLink,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { fsApi } from '../../featurestore/api';
import {
  AccessModifier,
  ActivePermission,
  CoreService,
  FeatureSet,
  ListableProject,
  Project,
  ProjectSearchRequestProjectSortField,
  ProjectSearchResults,
  SortDirection,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  accessModifierNameMap,
  activePermissionNameMap,
  CommonImageSrc,
  ContentsTitle,
  defaultPageSize,
  SortInfo,
} from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';
import { ListCellImage } from '../ListCellImage/ListCellImage';
import { RequestPermissionPanel } from '../RequestPermissionPanel/RequestPermissionPanel';
import { IProjectListClassNames, IProjectListStyles, projectListStylesDefault } from './ProjectList.styles';

const isDisabled = (item: ProjectWithUserNames, permission?: ActivePermission) =>
  item.accessModifier === AccessModifier.ACCESS_MODIFIER_PROJECT_ONLY &&
  permission !== ActivePermission.ACTIVE_PERMISSION_OWNER &&
  item.permission === ActivePermission.ACTIVE_PERMISSION_NONE;

const getProjectColumns = (
  isCardView: boolean,
  selectedId: string,
  onRequestAccess: (id: string, name: string) => void,
  onColumnClick: (e: React.MouseEvent<HTMLElement, MouseEvent>, col: IColumn) => void,
  onSelect?: (projectId: string, project?: Project) => void,
  onView?: (id: string) => void,
  requiredPermission?: ActivePermission,
  sortColumn?: IColumn
): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [];
  if (onSelect) {
    columns.push({
      key: 'select',
      name: 'Select',
      minWidth: 1,
      maxWidth: 1,
      onRender: (item) => (
        <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
          <Checkbox
            name="id"
            checked={item.id === selectedId}
            styles={[checkboxStylesCircle, { checkmark: { fontWeight: FontWeights.bold } }]}
            onChange={() => onSelect(item.id, item)}
          />
        </div>
      ),
    });
  }
  if (isCardView) {
    columns.push({
      key: 'name',
      name: 'Project Name',
      fieldName: 'description',
      minWidth: 200,
      data: {
        headerFieldName: 'name',
        listCellProps: {
          emptyMessage: 'No Description',
          onRenderPrefix: !onSelect
            ? (item) =>
                item.name?.toLowerCase().includes('h2o') ? (
                  <ListCellImage imageSrc={CommonImageSrc.logo512} />
                ) : (
                  <ListCellImage iconName="FormLibraryMirrored" />
                )
            : undefined,
        },
      },
    });
  } else {
    columns.push(
      {
        key: 'name',
        name: 'Project Name',
        fieldName: 'name',
        minWidth: 200,
        headerClassName: 'is-sortable',
        iconName: 'Sort',
        onColumnClick,
      },
      {
        key: 'description',
        name: 'Description',
        fieldName: 'description',
        minWidth: 200,
        headerClassName: 'is-sortable',
        iconName: 'Sort',
        onColumnClick,
        data: {
          listCellProps: { emptyMessage: 'No Description' },
        },
      }
    );
  }

  if (!onSelect) {
    columns.push(
      {
        key: 'accessModifier',
        name: 'Access Modifier',
        fieldName: 'accessModifier',
        headerClassName: 'is-sortable',
        iconName: 'Sort',
        minWidth: 130,
        maxWidth: 130,
        onColumnClick,
        onRender: (item) => (
          <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
            {isCardView && (
              <div
                style={{
                  fontWeight: FontWeights.semiBold,
                  paddingBottom: 6,
                }}
              >
                Access Modifier
              </div>
            )}
            <div
              style={{
                height: 18,
                color: defaultSemanticColors.textSecondary,
                ...(CommonStyles.truncateString as CSSProperties),
              }}
            >
              {accessModifierNameMap.get(item.accessModifier)}
            </div>
          </div>
        ),
      },
      {
        key: 'featureSetsCount',
        name: 'Feature Sets',
        fieldName: 'featureSetsCount',
        minWidth: 130,
        maxWidth: 130,
      },
      {
        key: 'author',
        name: 'Author',
        fieldName: 'authorName',
        headerClassName: 'is-sortable',
        iconName: 'Sort',
        minWidth: 100,
        onColumnClick,
      },
      {
        key: 'lastUpdateDateTime',
        name: 'Updated On',
        fieldName: 'lastUpdateDateTime',
        headerClassName: 'is-sortable',
        iconName: 'Sort',
        minWidth: 180,
        onColumnClick,
        data: {
          listCellProps: {
            emptyMessage: '-',
            onRenderText: (item: Project) => <>{formatDefaultDateAndTimeWithSeconds(item.lastUpdateDateTime)}</>,
          },
        },
      },
      {
        key: 'AccessType',
        name: 'Access Type',
        fieldName: 'permission',
        minWidth: 150,
        maxWidth: 150,
        onRender: (item) => {
          return item.permission !== ActivePermission.ACTIVE_PERMISSION_NONE ? (
            <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
              {isCardView && (
                <div
                  style={{
                    fontWeight: FontWeights.semiBold,
                    paddingBottom: 6,
                  }}
                >
                  Access Type
                </div>
              )}
              <div
                style={{
                  height: 18,
                  color: defaultSemanticColors.textSecondary,
                  ...(CommonStyles.truncateString as CSSProperties),
                }}
              >
                {activePermissionNameMap.get(item.permission)}
              </div>
            </div>
          ) : (
            <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
              <Link onClick={() => onRequestAccess(item.id, item.name)}>Request Access</Link>
            </div>
          );
        },
      },
      {
        key: 'view',
        name: 'View',
        minWidth: 160,
        maxWidth: 160,
        onRender: (item: ListableProject) => {
          const { id = '' } = item;
          return (
            <div
              style={{
                ...(CommonStyles.flexCenterVertically as CSSProperties),
                width: '100%',
                textAlign: 'right',
              }}
            >
              <div>
                <Link
                  styles={linkStylesGhost}
                  onClick={onView ? () => onView(id) : undefined}
                  disabled={isDisabled(item, requiredPermission)}
                >
                  View
                </Link>
              </div>
            </div>
          );
        },
      }
    );
  }
  if (sortColumn) {
    for (let i = 0; i < columns.length; i++) {
      if (sortColumn.key === columns[i].key) {
        columns[i] = { ...columns[i], ...sortColumn };
        break;
      }
    }
  }
  return columns;
};

const getFeatureSetColumns = (projectColumns: IColumnWithDataType[]): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'featureSetName',
      name: 'Feature Set Name',
      fieldName: 'description',
      minWidth: 350,
      data: {
        headerFieldName: 'featureSetName',
        listCellProps: {
          emptyMessage: 'No Description',
        },
      },
    },
    {
      key: 'version',
      name: 'Latest Version',
      fieldName: 'version',
      minWidth: 135,
      maxWidth: 135,
    },
    {
      key: 'author',
      name: 'Author',
      fieldName: 'authorName',
      minWidth: 105,
      maxWidth: 105,
    },
    {
      key: 'updatedOn',
      name: 'Updated On',
      fieldName: 'lastUpdateDateTime',
      minWidth: 270,
      maxWidth: 270,
      data: {
        listCellProps: {
          emptyMessage: '-',
          onRenderText: (item: FeatureSet) => <>{formatDefaultDateAndTimeWithSeconds(item.lastUpdateDateTime)}</>,
        },
      },
    },
    {
      key: 'view',
      name: 'View',
      minWidth: 130,
      maxWidth: 130,
      onRender: (item: FeatureSet) => {
        const { id } = item;
        return (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              width: '100%',
            }}
          >
            <div>
              <RouterLink styles={linkStylesGhost} to={`${RoutePaths.FEATURESTORE_FEATURE_SET}/${id}`}>
                View
              </RouterLink>
            </div>
          </div>
        );
      },
    },
  ];

  return adjustColumnWidth(getRowWidthByColumns(projectColumns), columns, [1, 2, 3, 4]);
};

interface ProjectWithUserNames extends ListableProject {
  authorName?: string;
}

const convertProjectToProjectWithUserNames = (project: ListableProject): ProjectWithUserNames => ({
  ...project,
  authorName: project.author?.name,
});

const fieldSortTypeMap = new Map([
  ['name', ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_NAME],
  ['description', ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_DESCRIPTION],
  ['accessModifier', ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_ACCESS_MODIFIER],
  ['author', ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_AUTHOR],
  ['lastUpdateDateTime', ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_LAST_UPDATE],
]);

interface IProjectListProps {
  isCardView?: boolean;
  searchText?: string;
  id?: string;
  requiredPermission?: ActivePermission;
  reload?: boolean;
  hasDisabledRow?: boolean;
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
  onSelect?: (projectId: string, project?: Project) => void;
  sortInfo?: SortInfo;
}

export const ProjectList = ({
  isCardView = true,
  id,
  searchText,
  requiredPermission,
  reload,
  hasDisabledRow = true,
  sortInfo,
  onUpdateData,
  onSelect,
}: IProjectListProps) => {
  const [groupsAndItems, setGroupsAndItems] = useState<GroupsAndItems>({
    groups: [],
    items: [],
  });
  const history = useHistory();
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [showRequestAccessPanel, setShowRequestAccessPanel] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const refMeta = useRef<{
    sortInfo?: SortInfo;
    sortColumn?: IColumn;
    columns?: IColumn[];
    selectedIdName: { id: string; name: string };
    isLoadingPage: boolean;
    pageSize: number;
  }>({
    selectedIdName: { id: '', name: '' },
    isLoadingPage: false,
    pageSize: defaultPageSize,
  });
  const { addToast } = useToast();
  const load = useCallback(
    async ({ groups, items, pageToken }: GroupsAndItems = { groups: [], items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading projects...`);
      try {
        const isFirstPage = groups.length === 0;
        const pageSize = refMeta.current.pageSize;
        const sort = refMeta.current.sortInfo;
        const hasSort =
          sort && sort.field && sort.field !== ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_UNDEFINED;
        const { listableProject = [], nextPageToken = '' }: ProjectSearchResults = await fsApi(
          CoreService.SearchProjects,
          {
            query: searchText,
            requiredPermission,
            pageSize,
            pageToken,
            sortField: hasSort ? sort?.field : undefined,
            sortDirection: hasSort ? sort?.direction : undefined,
          }
        );
        const groupsOfPages = getGroupsWithEmptyGroupsOfNextPage(
          groups,
          listableProject,
          pageSize,
          convertProjectToProjectWithUserNames,
          'name',
          onSelect ? '' : 'featureSetsCount'
        );
        let newData: GroupsAndItems = {
          groups: [...groupsOfPages],
          items: [...items],
          pageToken: nextPageToken,
        };
        if (isFirstPage && id) {
          const targetGroup = newData.groups.find((group) => group.data.data.id === id);
          if (targetGroup && targetGroup.count) {
            const project = targetGroup.data.data;
            const { featureSets = [] } = await fsApi(CoreService.ListFeatureSetsPage, {
              projectNames: [project.name || ''],
              pageSize: +(project.featureSetsCount || 0),
            });
            newData = addGroupItems(targetGroup, newData, targetGroup.startIndex, featureSets);
          }
        }
        setGroupsAndItems(newData);
        onUpdateData && onUpdateData(newData.groups.length, pageSize, newData.pageToken || '', ContentsTitle.projects);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading projectList: `));
      } finally {
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast, onUpdateData, searchText, requiredPermission]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const loadChildren = useCallback(
    async (group: IGroup, groupsAndItems: GroupsAndItems) => {
      try {
        const project = group.data.data;
        const { featureSets = [] } = await fsApi(CoreService.ListFeatureSetsPage, {
          projectNames: [project.name || ''],
          pageSize: +(project.featureSetsCount || 0),
        });
        setGroupsAndItems(addGroupItems(group, groupsAndItems, group.startIndex, featureSets));
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading FeatureSets: `));
      }
    },
    [addToast]
  );
  const onSelectProject = useCallback(
    (id: string, project?: Project) => {
      if (onSelect) {
        setSelectedId(id);
        onSelect(id, project);
      }
    },
    [onSelect]
  );
  const onRequestAccess = useCallback((id: string, name: string) => {
    refMeta.current.selectedIdName = { id, name };
    setShowRequestAccessPanel(true);
  }, []);
  const onView = useCallback((id: string) => {
    history.push(`${RoutePaths.FEATURESTORE_PROJECT}/${id}`);
  }, []);
  const onColumnClick = useCallback(
    (_e, col) => {
      const column = refMeta.current.columns?.find((c: IColumn) => c.key === col.key);
      if (column) {
        if (!column.isSorted) {
          column.isSorted = true;
          column.isSortedDescending = false;
        } else if (!column.isSortedDescending) {
          column.isSortedDescending = true;
        } else {
          column.isSorted = false;
        }
        refMeta.current.sortInfo = column.isSorted
          ? {
              field: fieldSortTypeMap.get(column.key),
              direction: column.isSortedDescending ? SortDirection.SORT_DESC : SortDirection.SORT_ASC,
            }
          : undefined;
      }
      refMeta.current.sortColumn = column ? { ...column } : undefined;
      load();
    },
    [load]
  );
  refMeta.current.columns = getProjectColumns(
    isCardView,
    selectedId,
    onRequestAccess,
    onColumnClick,
    onSelect ? onSelectProject : undefined,
    onView,
    requiredPermission,
    refMeta.current.sortColumn
  );
  const detailsListProps: IDetailsListProps = {
    styles: isCardView ? detailsListStylesGroupCard : detailsListStylesGroup,
    headerStyles: detailsHeaderStylesGroup,
    columns: refMeta.current.columns,
    isHeaderVisible: !isCardView,
    groups: groupsAndItems.groups,
    items: groupsAndItems.items,
    rowType: isCardView ? ListRowType.GroupHeaderCard : ListRowType.GroupHeader,
    getGroupHeight: isCardView
      ? (group: IGroup) => getGroupHeight(group)
      : (group: IGroup) => getGroupHeight(group, ListRowType.GroupHeader),
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    groupProps: {
      collapseAllVisibility: CollapseAllVisibility.hidden,
      showEmptyGroups: true,
      onRenderHeader: (props?: IDetailsGroupDividerProps) => {
        const { columns = [], group, onToggleCollapse } = props || {};
        const { data, isCollapsed, count } = group as IGroupWithDataType;
        if (!data.hasGroupHeaderData && !refMeta.current.isLoadingPage) {
          refMeta.current.isLoadingPage = true;
          onLoad(groupsAndItems, false);
        }
        return (
          <ListRow
            styles={isCardView ? undefined : listRowStylesThin}
            disabled={hasDisabledRow && isDisabled(data.data, requiredPermission)}
            data={data.data}
            columns={columns}
            group={onSelect ? undefined : group}
            collapsed={isCollapsed}
            title="Feature Sets"
            type={isCardView ? ListRowType.GroupHeaderCard : ListRowType.GroupHeader}
            hasOpenClose={count > 0}
            openCloseByButton={!!onSelect}
            hasLeftOpenClose={!isCardView}
            hideHeader={!isCardView}
            loading={!data.hasGroupHeaderData}
            loadingMessageForChildRows={!data.isLoaded ? 'loading...' : ''}
            onClick={onSelect ? () => onSelectProject(data.data.id, data.data) : undefined}
            onOpen={() => {
              if (group && isCollapsed && !data.isLoaded) {
                loadChildren(group, groupsAndItems);
              } else {
                onToggleCollapse!(group!);
              }
            }}
          />
        );
      },
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      return (
        <ListRow
          data={{ ...item, authorName: item.author?.name }}
          columns={getFeatureSetColumns(columns)}
          type={ListRowType.Group}
        />
      );
    },
  };
  useEffect(() => {
    refMeta.current.sortInfo = { ...sortInfo };
    load();
    return () => {
      setGroupsAndItems({
        groups: [],
        items: [],
      });
    };
  }, [load, sortInfo, reload]);
  useEffect(() => {
    reload && load();
  }, [reload]);
  const classNames = useClassNames<IProjectListStyles, IProjectListClassNames>('ProjectList', projectListStylesDefault);
  return (
    <div className={classNames.root} data-test="ProjectList">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : isCardView ? (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="details-list-card-view">
          {!loadingMessage && groupsAndItems.groups.length === 0 ? (
            <EmptyMessage message="No Projects Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      ) : (
        <div className={classNames.listView}>
          <div className={classNames.listViewWrap} data-is-scrollable="true" data-test="details-list-list-view">
            {!loadingMessage && groupsAndItems.groups.length === 0 ? (
              <EmptyMessage message="No Projects Available" />
            ) : (
              <DetailsList {...detailsListProps} />
            )}
          </div>
        </div>
      )}
      <RequestPermissionPanel
        projectId={refMeta.current.selectedIdName.id}
        name={refMeta.current.selectedIdName.name}
        show={showRequestAccessPanel}
        onDismiss={(_e, updated) => {
          if (updated) {
            load();
          }
          setShowRequestAccessPanel(false);
        }}
      />
    </div>
  );
};
